import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import api from '../api/client'
import { salidasApi } from '../api/salidas'
import { remitosApi } from '../api/remitos'
import Boton from '../components/Boton'
import FrenoEntregaAnticipada from '../components/FrenoEntregaAnticipada'
import { hoyISO, fmtMonto } from '../utils/fecha'

export default function SalidaFormPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const preseleccion = location.state?.preseleccion   // [{ entidad_tipo, entidad_id, label, sub, calle, localidad, cp, zona }]
  // Desde la asignación rápida con Andreani (20 oct 2026): llega con el
  // fletero ya elegido para todos y cotiza sola — la salida se arma después
  // de ver la cotización, nunca antes.
  const fleteroInicial = location.state?.fletero_id ? String(location.state.fletero_id) : ''

  useEffect(() => {
    if (!preseleccion?.length) navigate('/asignacion', { replace: true })
  }, [])

  const [fleteros, setFleteros] = useState([])
  const [loading, setLoading]   = useState(true)
  const [enviando, setEnviando] = useState(false)
  const [error, setError]       = useState('')
  const [progreso, setProgreso] = useState('')
  const [frenos, setFrenos]     = useState([]) // envíos con fecha pactada posterior (entrega anticipada)

  // Todos seleccionados por defecto
  const [seleccionados, setSeleccionados] = useState(
    () => new Set((preseleccion || []).map(d => `${d.entidad_tipo}:${d.entidad_id}`))
  )

  // Fletero asignado por documento: { [key]: fletero_id }
  const [asignaciones, setAsignaciones] = useState(
    () => fleteroInicial ? Object.fromEntries((preseleccion || []).map(d => [`${d.entidad_tipo}:${d.entidad_id}`, fleteroInicial])) : {}
  )
  const [bulkFletero, setBulkFletero] = useState(fleteroInicial)
  // Fletero elegido por zona: { [zonaNombre]: fletero_id }
  const [zonaFletero, setZonaFletero] = useState({})

  const [form, setForm] = useState({
    fecha: location.state?.fecha || hoyISO(),
    notas: location.state?.notas || '',
  })

  // Lo que cobra cada fletero EXTERNO por su salida (Martin, 3 oct 2026):
  // { [fleteroId]: monto }. Obligatorio para crearla.
  const [costos, setCostos] = useState({})
  // Comparativo con Andreani: el COSTO de Ligier (sin IVA, con su seguro) de
  // cada envío seleccionado. { filas, total } o null si no se cotizó.
  const [andreani, setAndreani] = useState(null)
  const [cotizando, setCotizando] = useState(false)

  // Puntos de retiro (config/sucursales.php). Regla de Martin (25 ago 2026):
  // un "envío" a un canal interno NO arma salida — el remito pasa a RETIRO en
  // la sucursal (remitos.retira + sucursal_retiro). Por eso los fleteros
  // internos no se ofrecen acá y en su lugar van las sucursales.
  const [sucursales, setSucursales] = useState([])
  useEffect(() => {
    api.get('/fleteros')
      .then(r => setFleteros((r.data.data || r.data).filter(f => !f.interno)))
      .catch(() => setError('Error cargando fleteros.'))
      .finally(() => setLoading(false))
    api.get('/sucursales-retiro').then(r => setSucursales(r.data || [])).catch(() => {})
  }, [])
  const esRetiro = (fid) => String(fid || '').startsWith('retiro:')
  const opcionesDestino = (
    <>
      {fleteros.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
      {sucursales.length > 0 && (
        <optgroup label="Retiro en sucursal (sin salida)">
          {sucursales.map(sc => <option key={sc.id} value={`retiro:${sc.id}`}>🏬 Retira en {sc.nombre}</option>)}
        </optgroup>
      )}
    </>
  )

  const key = (d) => `${d.entidad_tipo}:${d.entidad_id}`

  const toggleDoc = (k) =>
    setSeleccionados(prev => {
      const s = new Set(prev)
      s.has(k) ? s.delete(k) : s.add(k)
      return s
    })

  const toggleTodos = () =>
    setSeleccionados(
      seleccionados.size === preseleccion.length
        ? new Set()
        : new Set(preseleccion.map(key))
    )

  const setFleteroDoc = (k, fleteroId) =>
    setAsignaciones(prev => ({ ...prev, [k]: fleteroId }))

  // Asigna el fletero elegido a todos los documentos seleccionados
  const asignarBulk = () => {
    if (!bulkFletero) return
    setAsignaciones(prev => {
      const next = { ...prev }
      preseleccion.forEach(d => { if (seleccionados.has(key(d))) next[key(d)] = bulkFletero })
      return next
    })
  }

  // Asigna un fletero a todos los documentos seleccionados de UNA zona
  const asignarPorZona = (zonaNombre, fleteroId) => {
    if (!zonaNombre || !fleteroId) return
    setAsignaciones(prev => {
      const next = { ...prev }
      preseleccion.forEach(d => {
        if (seleccionados.has(key(d)) && (d.zona || 'Sin zona') === zonaNombre) next[key(d)] = fleteroId
      })
      return next
    })
  }

  const nombreFletero = (id) => esRetiro(id)
    ? `Retiro en ${sucursales.find(sc => sc.id === String(id).slice(7))?.nombre || 'sucursal'}`
    : (fleteros.find(f => String(f.id) === String(id))?.nombre || '')

  // Zonas presentes en los documentos seleccionados, con su conteo
  const zonasEnSeleccion = (() => {
    const m = new Map()
    ;(preseleccion || []).forEach(d => {
      if (!seleccionados.has(key(d))) return
      const z = d.zona || 'Sin zona'
      m.set(z, (m.get(z) || 0) + 1)
    })
    return [...m.entries()].map(([zona, n]) => ({ zona, n })).sort((a, b) => a.zona.localeCompare(b.zona))
  })()

  // Agrupa los seleccionados por fletero -> cuántas salidas se crearán
  const docsSeleccionados = preseleccion ? preseleccion.filter(d => seleccionados.has(key(d))) : []
  const grupos = docsSeleccionados.reduce((acc, d) => {
    const fid = asignaciones[key(d)]
    if (!fid) return acc
    ;(acc[fid] ||= []).push(d)
    return acc
  }, {})
  const sinFletero = docsSeleccionados.filter(d => !asignaciones[key(d)]).length
  // Un retiro en sucursal no requiere zona → no cuenta como "sin zona". Un
  // envío por Andreani tampoco: la zona es del reparto propio.
  const vaPorAndreani = (d) => !!fleteros.find(f => String(f.id) === String(asignaciones[key(d)]))?.es_andreani
  const sinZona = docsSeleccionados.filter(d => !d.zona && !esRetiro(asignaciones[key(d)]) && !vaPorAndreani(d)).length
  const cantSalidas = Object.keys(grupos).filter(k => !esRetiro(k)).length

  // Entrega antes de la fecha pactada (30 oct 2026): se avisa APENAS se elige
  // el envío, no al apretar Crear. Los que van a retiro no cuentan; sin
  // fletero todavía, cuenta como reparto propio.
  const firmaVerificacion = JSON.stringify([form.fecha, docsSeleccionados
    .filter(d => !esRetiro(asignaciones[key(d)]))
    .map(d => [key(d), asignaciones[key(d)] || ''])])
  useEffect(() => {
    let vigente = true
    const t = setTimeout(async () => {
      const porFletero = {}
      for (const d of docsSeleccionados) {
        const fid = asignaciones[key(d)] || ''
        if (esRetiro(fid)) continue
        ;(porFletero[fid] ||= []).push({ entidad_tipo: d.entidad_tipo, entidad_id: d.entidad_id })
      }
      const encontrados = []
      try {
        for (const [fid, documentos] of Object.entries(porFletero)) {
          const { data } = await salidasApi.verificarAnticipadas({ fletero_id: fid ? Number(fid) : null, fecha: form.fecha, documentos })
          encontrados.push(...(data.entrega_anticipada || []))
        }
        if (vigente) setFrenos(encontrados)
      } catch { /* si la verificación falla, el freno del backend al crear sigue en pie */ }
    }, 300)
    return () => { vigente = false; clearTimeout(t) }
  }, [firmaVerificacion]) // eslint-disable-line react-hooks/exhaustive-deps
  // Zona Interior = entrega a más de ~50 km → no corresponde transporte
  // propio, va por Andreani (sugerencia #47). Alerta, no bloqueo.
  const fueraReparto = docsSeleccionados.filter(d => d.fuera_reparto)

  const fleteroDe = (id) => fleteros.find(f => String(f.id) === String(id))
  const externosSinCosto = Object.keys(grupos).filter(fid => fleteroDe(fid)?.externo && !(Number(costos[fid]) > 0))
  // Costo Andreani de un grupo de documentos (con la última cotización).
  const andreaniDe = (docs) => {
    if (!andreani) return null
    const filas = docs.map(d => andreani.filas.find(f => f.tipo === d.entidad_tipo && f.id === d.entidad_id))
    if (filas.some(f => !f)) return null
    return { total: filas.reduce((s, f) => s + (f.costo || 0), 0), sinCotizar: filas.filter(f => f.costo == null).length }
  }

  // Total de la cotización solo con los envíos que siguen tildados: destildar
  // una fila de la tabla saca el envío de la cotización y de la salida.
  const andreaniSel = (() => {
    const filas = (andreani?.filas || []).filter(f => seleccionados.has(`${f.tipo}:${f.id}`))
    return { n: filas.length, total: filas.reduce((s, f) => s + (f.costo || 0), 0), sinCotizar: filas.filter(f => f.costo == null).length }
  })()

  const cotizarAndreani = async () => {
    if (!docsSeleccionados.length) return
    setCotizando(true); setError('')
    try {
      const { data } = await salidasApi.cotizarAndreani(docsSeleccionados.map(d => ({ tipo: d.entidad_tipo, id: d.entidad_id })))
      setAndreani(data)
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo cotizar con Andreani.')
    } finally { setCotizando(false) }
  }

  // Llegó desde la asignación rápida con Andreani: cotiza una sola vez al abrir.
  const cotizoAlAbrir = useRef(false)
  useEffect(() => {
    if (location.state?.cotizar && !cotizoAlAbrir.current && docsSeleccionados.length) {
      cotizoAlAbrir.current = true
      cotizarAndreani()
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!docsSeleccionados.length) { setError('Seleccioná al menos un documento.'); return }
    if (sinZona > 0) { setError(`${sinZona} documento(s) sin zona. Asigná la zona en /asignacion antes de despachar.`); return }
    if (sinFletero > 0) { setError(`Faltan asignar fletero a ${sinFletero} documento(s).`); return }
    if (externosSinCosto.length) { setError(`Cargá lo que cobra ${externosSinCosto.map(nombreFletero).join(', ')} por su salida.`); return }
    setError('')
    setEnviando(true)
    try {
      const idsCreadas = []
      const entradas = Object.entries(grupos)
      // ¿Alguno sale antes de la fecha pactada con el cliente? Se pregunta
      // ANTES de crear las salidas para no dejar ninguna a medio armar.
      const frenados = []
      for (const [fleteroId, docs] of entradas) {
        if (esRetiro(fleteroId)) continue
        const { data: chk } = await salidasApi.verificarAnticipadas({
          fletero_id: Number(fleteroId),
          fecha: form.fecha,
          documentos: docs.map(d => ({ entidad_tipo: d.entidad_tipo, entidad_id: d.entidad_id })),
        })
        frenados.push(...(chk.entrega_anticipada || []))
      }
      if (frenados.length) {
        setFrenos(frenados)
        setEnviando(false)
        return
      }
      let i = 0
      let retiros = 0
      for (const [fleteroId, docs] of entradas) {
        i++
        if (esRetiro(fleteroId)) {
          // Retiro en sucursal: no hay salida. Cada remito pasa a retiro (si
          // estaba en un viaje, el backend lo libera). Las NE no aplican.
          const sucursalId = String(fleteroId).slice(7)
          setProgreso(`Pasando a retiro ${i}/${entradas.length} — ${nombreFletero(fleteroId)} (${docs.length} doc.)`)
          for (const doc of docs) {
            if (doc.entidad_tipo !== 'remito') throw new Error(`${doc.label || doc.sub}: una nota de envío no puede pasar a retiro (solo remitos T90).`)
            await remitosApi.pasarARetiro(doc.entidad_id, sucursalId, form.notas)
            retiros++
          }
          continue
        }
        setProgreso(`Creando salida ${i}/${entradas.length} — ${nombreFletero(fleteroId)} (${docs.length} doc.)`)
        const { data: salida } = await salidasApi.crear({
          fletero_id: fleteroId,
          fecha: form.fecha,
          notas: form.notas,
          ...(fleteroDe(fleteroId)?.externo ? { costo: Number(costos[fleteroId]) } : {}),
        })
        for (const doc of docs) {
          await salidasApi.agregarDocumento(salida.id, {
            entidad_tipo: doc.entidad_tipo,
            entidad_id: doc.entidad_id,
          })
        }
        await salidasApi.despachar(salida.id)
        idsCreadas.push(salida.id)
      }
      if (idsCreadas.length === 0 && retiros > 0) navigate('/logistica/distribucion')
      else navigate(idsCreadas.length === 1 ? `/salidas/${idsCreadas[0]}` : '/salidas')
    } catch (err) {
      if (err.response?.data?.entrega_anticipada) setFrenos(err.response.data.entrega_anticipada)
      setError(err.response?.data?.message || err.message || 'Error al crear las salidas.')
      setEnviando(false)
      setProgreso('')
    }
  }

  if (!preseleccion?.length) return null

  const docs_ne  = preseleccion.filter(d => d.entidad_tipo === 'nota_envio')
  const docs_r90 = preseleccion.filter(d => d.entidad_tipo === 'remito')
  const totalSel = seleccionados.size

  const renderFila = (d, accent) => {
    const k = key(d)
    const sel = seleccionados.has(k)
    const fid = asignaciones[k] || ''
    const falta = sel && !fid
    return (
      <div key={k} style={{ ...st.docRow, background: sel ? accent.bg : '#f8fafc', border: `1px solid ${falta ? '#ef4444' : (sel ? accent.bd : '#e2e8f0')}` }}>
        <input type="checkbox" checked={sel} onChange={() => toggleDoc(k)} />
        <code style={st.docLabel} onClick={() => toggleDoc(k)}>{d.label}</code>
        <span style={st.docSub} onClick={() => toggleDoc(k)} title={d.sub}>{d.sub}</span>
        <span style={st.docDir} title={[d.calle, d.localidad].filter(Boolean).join(', ')}>{[d.calle, d.localidad].filter(Boolean).join(', ') || '—'}</span>
        <span style={st.docCp}>CP {d.cp || '—'}</span>
        <span
          style={d.fuera_reparto ? st.docZonaFuera : (d.zona ? st.docZona : st.docZonaVacia)}
          title={d.fuera_reparto ? 'Entrega a más de ~50 km — no corresponde transporte propio, va por Andreani' : undefined}
        >
          {d.fuera_reparto ? `⚠ ${d.zona}` : (d.zona || 'Sin zona')}
        </span>
        <select
          value={fid}
          onChange={e => setFleteroDoc(k, e.target.value)}
          style={{ ...st.fleteroSelect, borderColor: falta ? '#ef4444' : '#cbd5e1' }}
          disabled={!sel || loading}
        >
          <option value="">Fletero…</option>
          {opcionesDestino}
        </select>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Boton variante="ghost" sm onClick={() => navigate('/asignacion')}>←</Boton>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>Nueva salida</h1>
      </div>

      <FrenoEntregaAnticipada frenos={frenos} />
      {error && <div style={st.error}>{error}</div>}

      {fueraReparto.length > 0 && (
        <div style={st.avisoAndreani}>
          ⚠ {fueraReparto.length === 1
            ? `El documento ${fueraReparto[0].label} tiene entrega`
            : `${fueraReparto.length} documentos (${fueraReparto.map(d => d.label).join(', ')}) tienen entrega`}
          {' '}a más de ~50 km (zona Interior): no corresponde transporte propio — el envío va por Andreani.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={st.card}>
          <h2 style={st.cardTitle}>Datos de salida</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={lbl}>Fecha *</label>
              <input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} style={inp} required />
            </div>
            <div>
              <label style={lbl}>Notas</label>
              <input value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} style={inp} placeholder="Opcional" />
            </div>
          </div>
          <p style={st.hint}>La fecha y las notas se aplican a todas las salidas generadas.</p>
        </div>

        <div style={st.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <h2 style={{ ...st.cardTitle, margin: 0 }}>
              Documentos — {totalSel} de {preseleccion.length} seleccionados
            </h2>
            <label style={st.checkAll}>
              <input type="checkbox" checked={totalSel === preseleccion.length} onChange={toggleTodos} />
              Todos
            </label>
          </div>

          {/* Asignación masiva de fletero */}
          <div style={st.bulkRow}>
            <span style={st.bulkLabel}>Asignar fletero a los {totalSel} seleccionados:</span>
            <select value={bulkFletero} onChange={e => setBulkFletero(e.target.value)} style={st.bulkSelect} disabled={loading}>
              <option value="">Elegir fletero…</option>
              {opcionesDestino}
            </select>
            <Boton variante="secondary" sm type="button" onClick={asignarBulk} disabled={!bulkFletero || !totalSel}>Aplicar a todos</Boton>
          </div>

          {/* Asignación por zona: cada zona → un fletero */}
          {zonasEnSeleccion.length >= 1 && (
            <div style={st.zonaAsignBox}>
              <p style={st.zonaAsignTitle}>⚡ Asignar por zona — todos los remitos de una zona a un fletero</p>
              <div style={st.zonaAsignList}>
                {zonasEnSeleccion.map(({ zona, n }) => (
                  <div key={zona} style={st.zonaAsignRow}>
                    <span style={st.zonaAsignNombre}>
                      {zona} <span style={st.zonaAsignCount}>({n})</span>
                    </span>
                    <select
                      value={zonaFletero[zona] || ''}
                      onChange={e => {
                        const fid = e.target.value
                        setZonaFletero(prev => ({ ...prev, [zona]: fid }))
                        asignarPorZona(zona, fid)
                      }}
                      style={st.bulkSelect}
                      disabled={loading}
                    >
                      <option value="">Elegir fletero…</option>
                      {opcionesDestino}
                    </select>
                  </div>
                ))}
              </div>
              <p style={st.zonaAsignHint}>Al elegir un fletero, todos los documentos de esa zona quedan asignados.</p>
            </div>
          )}

          {docs_ne.length > 0 && (
            <div style={{ marginBottom: docs_r90.length ? 14 : 0 }}>
              <p style={st.groupLabel}>Notas de envío</p>
              <div style={st.docList}>
                {docs_ne.map(d => renderFila(d, { bg: '#ede9fe', bd: '#8b5cf6' }))}
              </div>
            </div>
          )}

          {docs_r90.length > 0 && (
            <div>
              <p style={st.groupLabel}>Remitos T90</p>
              <div style={st.docList}>
                {docs_r90.map(d => renderFila(d, { bg: '#fff7ed', bd: '#f59e0b' }))}
              </div>
            </div>
          )}
        </div>

        {/* Comparativo con Andreani + lo que cobra cada fletero externo */}
        <div style={st.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ ...st.cardTitle, margin: 0 }}>Costo — ¿fletero o Andreani?</h2>
            <Boton variante="secondary" sm type="button" onClick={cotizarAndreani} disabled={cotizando || !docsSeleccionados.length}>
              {cotizando ? 'Cotizando…' : andreani ? '↻ Volver a cotizar por Andreani' : `Cotizar los ${docsSeleccionados.length} envíos por Andreani`}
            </Boton>
          </div>
          <p style={st.hint}>Andreani = lo que le cuesta a Ligier: tarifa sin IVA, con el seguro de Andreani. Misma cuenta que la cotización de la NV.</p>

          {Object.keys(grupos).filter(fid => fleteroDe(fid)?.externo).map(fid => {
            const docs = grupos[fid]
            const a = andreaniDe(docs)
            const monto = Number(costos[fid]) || 0
            return (
              <div key={fid} style={st.costoFila}>
                <span style={st.costoNombre}>{nombreFletero(fid)} <span style={st.zonaAsignCount}>· externo · {docs.length} envío(s)</span></span>
                <label style={st.costoLabel}>Lo que cobra por esta salida *</label>
                <input type="number" min="0" step="1" value={costos[fid] ?? ''} placeholder="$"
                  onChange={e => setCostos(c => ({ ...c, [fid]: e.target.value }))}
                  style={{ ...inp, width: 140, borderColor: externosSinCosto.includes(fid) ? '#ef4444' : '#e2e8f0' }} />
                {monto > 0 && <span style={st.costoDato}>${fmtMonto(monto / docs.length)} por envío</span>}
                {a && (
                  <span style={st.costoDato}>
                    Por Andreani: <b>${fmtMonto(a.total)}</b>{a.sinCotizar ? ` (${a.sinCotizar} sin cotizar)` : ''}
                    {monto > 0 && !a.sinCotizar && (
                      <b style={{ marginLeft: 8, color: a.total < monto ? '#166534' : '#334155' }}>
                        {a.total < monto ? `Andreani ahorra $${fmtMonto(monto - a.total)}` : `El fletero ahorra $${fmtMonto(a.total - monto)}`}
                      </b>
                    )}
                  </span>
                )}
              </div>
            )
          })}

          {andreani && (
            <table style={st.tabla}>
              <thead>
                <tr><th style={{ ...st.th, width: 28 }} title="Destildá para sacar el envío de la cotización y de la salida"></th><th style={st.th}>Doc.</th><th style={st.th}>Destino</th><th style={st.th}>CP</th><th style={{ ...st.th, textAlign: 'right' }}>Bot.</th><th style={{ ...st.th, textAlign: 'right' }}>Kg</th><th style={{ ...st.th, textAlign: 'right' }} title="Lo que asegura Andreani (sin IVA)">Valor declarado</th><th style={{ ...st.th, textAlign: 'right' }} title="1 % del valor declarado, incluido en el total">Seguro</th><th style={{ ...st.th, textAlign: 'right' }}>Andreani sin IVA</th></tr>
              </thead>
              <tbody>
                {andreani.filas.map(f => {
                  const k = `${f.tipo}:${f.id}`
                  const sel = seleccionados.has(k)
                  return (
                  <tr key={k} style={sel ? undefined : { opacity: 0.45 }}>
                    <td style={st.td}><input type="checkbox" checked={sel} onChange={() => toggleDoc(k)} title={sel ? 'Sacar de la cotización y de la salida' : 'Volver a incluir'} /></td>
                    <td style={{ ...st.td, fontFamily: 'monospace', fontWeight: 600, textDecoration: sel ? 'none' : 'line-through' }}>{f.tipo === 'nota_envio' ? 'NE ' : 'Re '}{f.numero}</td>
                    <td style={{ ...st.td, color: '#475569' }}>{f.destino || '—'}</td>
                    <td style={{ ...st.td, fontFamily: 'monospace' }}>{f.cp || '—'}</td>
                    <td style={{ ...st.td, textAlign: 'right', fontFamily: 'monospace' }}>{f.botellas}</td>
                    <td style={{ ...st.td, textAlign: 'right', fontFamily: 'monospace' }}>{Number(f.kg).toFixed(1)}</td>
                    <td style={{ ...st.td, textAlign: 'right', fontFamily: 'monospace' }}>{f.valor_declarado != null ? `$${fmtMonto(f.valor_declarado)}` : '—'}</td>
                    <td style={{ ...st.td, textAlign: 'right', fontFamily: 'monospace' }}>{f.seguro != null ? `$${fmtMonto(f.seguro)}` : '—'}</td>
                    <td style={{ ...st.td, textAlign: 'right', fontFamily: 'monospace' }}>{f.costo != null ? `$${fmtMonto(f.costo)}` : <span style={{ color: '#92400e' }}>{f.motivo}</span>}</td>
                  </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...st.td, fontWeight: 700 }} colSpan={8}>Total por Andreani ({andreaniSel.n} de {andreani.filas.length}){andreaniSel.sinCotizar ? ` · ${andreaniSel.sinCotizar} sin cotizar` : ''}</td>
                  <td style={{ ...st.td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>${fmtMonto(andreaniSel.total)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Resumen de salidas a generar */}
        <div style={st.resumen}>
          {frenos.length > 0
            ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>⚠ {frenos.length} envío(s) con la entrega pactada para más adelante: sacalos o pedí la autorización (arriba).</span>
            : sinZona > 0
            ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>⚠ {sinZona} documento(s) sin zona — asignala en /asignacion antes de despachar.</span>
            : sinFletero > 0
            ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>⚠ Faltan {sinFletero} documento(s) sin fletero.</span>
            : <span>Se generarán <b>{cantSalidas}</b> salida(s): {Object.entries(grupos).map(([fid, docs]) => `${nombreFletero(fid)} (${docs.length})`).join(' · ')}</span>}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
          {progreso && <span style={st.progreso}>{progreso}</span>}
          <Boton variante="ghost" type="button" onClick={() => navigate('/asignacion')}>Cancelar</Boton>
          <Boton variante="primary" type="submit" disabled={enviando || loading || !cantSalidas || sinFletero > 0 || sinZona > 0 || externosSinCosto.length > 0 || frenos.length > 0}>
            {enviando ? 'Creando...' : `Crear y despachar ${cantSalidas} salida(s)`}
          </Boton>
        </div>
      </form>
    </div>
  )
}

const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: 4 }
const inp = { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box' }

const st = {
  error:      { background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: '0.875rem' },
  card:       { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.06)', marginBottom: 20 },
  cardTitle:  { fontSize: '0.85rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', margin: '0 0 14px' },
  hint:       { fontSize: '0.74rem', color: '#94a3b8', margin: '10px 0 0' },
  groupLabel: { fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', margin: '0 0 6px', letterSpacing: '0.05em' },
  checkAll:   { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', fontWeight: 600, color: '#64748b', cursor: 'pointer' },
  bulkRow:    { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, padding: '8px 12px', marginBottom: 14 },
  bulkLabel:  { fontSize: '0.78rem', fontWeight: 600, color: '#475569' },
  bulkSelect: { padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem' },
  zonaAsignBox:   { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', marginBottom: 14 },
  zonaAsignTitle: { fontSize: '0.8rem', fontWeight: 700, color: '#1e40af', margin: '0 0 8px' },
  zonaAsignList:  { display: 'flex', flexDirection: 'column', gap: 6 },
  zonaAsignRow:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' },
  zonaAsignNombre:{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' },
  zonaAsignCount: { fontSize: '0.72rem', color: '#64748b', fontWeight: 600 },
  zonaAsignHint:  { fontSize: '0.72rem', color: '#64748b', margin: '8px 0 0' },
  docList:    { display: 'flex', flexDirection: 'column', gap: 5 },
  // Grilla de columnas fijas: el fletero queda en la misma columna en todas las filas;
  // lo que cede es el nombre/dirección (minmax(0,…) + ellipsis), nunca el select.
  docRow:     { display: 'grid', gridTemplateColumns: '16px 110px minmax(0, 1.1fr) minmax(0, 1.4fr) 92px 96px 160px', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 8 },
  docLabel:   { fontWeight: '700', fontSize: '0.875rem', color: '#1e293b', cursor: 'pointer', whiteSpace: 'nowrap' },
  docSub:     { fontSize: '0.78rem', color: '#64748b', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  docDir:     { fontSize: '0.78rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  docCp:      { justifySelf: 'start', fontSize: '0.72rem', fontWeight: '700', color: '#1e40af', background: '#eff6ff', padding: '1px 6px', borderRadius: 6, border: '1px solid #bfdbfe', fontFamily: 'monospace', whiteSpace: 'nowrap' },
  docZona:    { justifySelf: 'start', fontSize: '0.72rem', fontWeight: '600', color: '#475569', background: '#f1f5f9', padding: '1px 6px', borderRadius: 6, whiteSpace: 'nowrap' },
  docZonaVacia: { justifySelf: 'start', fontSize: '0.72rem', fontWeight: '600', color: '#b91c1c', background: '#fef2f2', padding: '1px 6px', borderRadius: 6, border: '1px solid #fecaca', whiteSpace: 'nowrap' },
  docZonaFuera: { justifySelf: 'start', fontSize: '0.72rem', fontWeight: '700', color: '#92400e', background: '#fef3c7', padding: '1px 6px', borderRadius: 6, border: '1px solid #fcd34d', whiteSpace: 'nowrap' },
  avisoAndreani: { background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: '0.82rem', fontWeight: 500 },
  fleteroSelect: { padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.78rem', width: '100%', minWidth: 0, background: '#fff' },
  resumen:    { background: '#f1f5f9', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.82rem', color: '#334155' },
  progreso:   { fontSize: '0.78rem', color: '#0f766e', fontWeight: 600 },
  costoFila:  { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 0', borderTop: '1px solid #f1f5f9', marginTop: 10 },
  costoNombre:{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', minWidth: 180 },
  costoLabel: { fontSize: '0.78rem', fontWeight: 600, color: '#475569' },
  costoDato:  { fontSize: '0.8rem', color: '#334155' },
  tabla:      { width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: '0.8rem' },
  th:         { textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', padding: '6px 8px', borderBottom: '1px solid #e2e8f0' },
  td:         { padding: '6px 8px', borderBottom: '1px solid #f1f5f9', color: '#1e293b' },
}
